import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, OrderLineModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered"; // Store Custom Code
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden"; // Store Custom Code
import { IconWrapper } from "@insite/mobius/Icon";
import { SelectPresentationProps } from "@insite/mobius/Select"; // Store Custom Code
import { TextFieldProps } from "@insite/mobius/TextField";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries"; // Store Custom Code
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import QuickOrderProductSwapButton from "@store/Components/Marketing/QuickOrderProductSwapButton";
import ProductPromotionTextBanner, {
    ProductPromotionTextBannerStyles,
} from "@store/Components/Store/ProductPromotionTextBanner"; // Store Custom Code
import ProductAddToCartButton from "@store/Overrides/Components/ProductAddToCartButton"; // Store Custom Code
import { ProductAvailabilityStyles } from "@store/Overrides/Components/ProductAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@store/Overrides/Components/ProductDescription"; // Store Custom Code
import ProductImage, { ProductImageStyles } from "@store/Overrides/Components/ProductImage"; // Store Custom Code
import ProductPartNumbers, { ProductPartNumbersStyles } from "@store/Overrides/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@store/Overrides/Components/ProductPrice"; // Store Custom Code
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@store/Overrides/Components/ProductQuantityBreakPricing"; // Store Custom Code
import { COLORS, TYPOGRAPHY } from "@store/theme/design"; // Store Custom Code
import React, { ReactNode, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    labelsVisible?: boolean;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showAvailability: boolean; // Store Custom Code
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    extendedStyles?: ProductCarouselProductSlimStyles; // Store Custom Code
    mobileAddOverride?: string; // Store Custom Code
    desktopAddOverride?: string; // Store Custom Code
    condensedActionsTemplate?: (product: ProductModel) => ReactNode;
    isQuickOrder?: boolean; // Store Custom Code
    parentProduct?: ProductContextModel; // Store Custom Code
    analyticsId?: string; // Store Custom Code
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductSlimStyles {
    container?: GridContainerProps;
    containerLower?: GridContainerProps; // Store Custom Code
    imageGridItem?: GridItemProps;
    productDetailsGridItem?: GridItemProps;
    productBreakPriceGridItem?: GridItemProps; // Store Custom Code
    productCartGridItem?: GridItemProps; // Store Custom Code
    productSwapGridItem?: GridItemProps; // Store Custom Code
    productImageStyles?: ProductImageStyles; // Store Custom Code
    pricingGridItem?: GridItemProps; // Store Custom Code
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productPriceStyles?: ProductPriceStyles;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productSwapButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    condensedActionsTemplateGridItem?: GridItemProps;
    productPromotionTextBannerStyles?: ProductPromotionTextBannerStyles;
    cartMobileHidden?: HiddenProps;
    cartDesktopHidden?: HiddenProps;
    productAvailabilityStyles?: ProductAvailabilityStyles; // Store Custom Code
}

// Store Custom Code
// Based on ProductCarouselProductCondensed
export const productCarouselProductSlimStyles: ProductCarouselProductSlimStyles = {
    container: {
        gap: 30,
        css: css`
            border: 1px solid ${COLORS.border.base};
            border-bottom: 0;
            padding: 15px;
            padding-bottom: 0;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            border: 0;
                        `,
                        css``,
                        css``,
                        css``,
                        css``,
                    ],
                    "max",
                )}
        `,
    },
    containerLower: {
        gap: 30,
        css: css`
            border: 1px solid ${COLORS.border.base};
            border-top: 0;
            padding: 15px;
            padding-top: 0;
            padding-bottom: 9px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            border: 0;
                        `,
                        css``,
                        css``,
                        css``,
                        css``,
                    ],
                    "max",
                )}
        `,
    },
    imageGridItem: {
        width: 4,
        css: css`
            flex-direction: column;
            align-items: center;
        `,
    },
    productBrandWrapper: {
        css: css``,
    },
    productDetailsGridItem: {
        width: 8,
        css: css`
            flex-direction: column;
            align-items: baseline;
        `,
    },
    pricingGridItem: {
        width: 4,
        css: css`
            flex-direction: column;
            align-items: baseline;
        `,
    },
    productSwapGridItem: {
        width: 8,
        css: css`
            flex-direction: column;
            align-items: center;
        `,
    },
    productCartGridItem: {
        width: 8,
        css: css`
            flex-direction: column;
            align-items: center;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin: 0;
                font-weight: ${TYPOGRAPHY.weight.black};
                font-size: ${TYPOGRAPHY.size.large};
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin: ${TYPOGRAPHY.spacing.small} 0;
                margin-bottom: 0;
            `,
        },
        erpNumberGridItem: {
            css: css`
                justify-content: left;
            `,
        },
        erpNumberValueText: {
            size: 13,
            css: css`
                flex: 0 0 auto;
                margin-left: 4px;
            `,
        },
        erpNumberLabelText: { size: 13 },
    },
    productPriceWrapper: {
        css: css`
            display: flex;
            flex-grow: 1;
            margin-bottom: 0;
            align-items: center;
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 6px;
            `,
        },
    },
    productBreakPriceGridItem: {
        width: 12,
        css: css`
            padding: 0 !important;
            > button {
                margin: 15px 15px 0 15px;
                margin-left: auto;
            }
        `,
    },
    productQuantityBreakPricingStyles: {
        viewLink: {},
    },
    addToCartContainer: {
        gap: 15,
        css: css`
            flex-grow: 0;
            margin-bottom: 6px;
        `,
        offsetProps: {
            style: {
                maxWidth: "105%", // Fix overflow in mobile edge case for small devices
            },
        },
    },
    qtyGridItem: { width: 3 },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
                input {
                    height: 40px;
                    min-width: 2em;
                }
            `,
        },
    },
    addToCartButtonGridItem: { width: 9 },
    productSwapButtonStyles: {
        css: css`
            width: 100%;
        `,
    },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;

            button {
                width: 100%;
            }
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: { size: 13 },
            css: css`
                margin-bottom: 12px;
            `,
        },
    },
    condensedActionsTemplateGridItem: {
        width: 12,
    },
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                width: 100%;
            `,
        },
        image: {
            css: css`
                img {
                    height: 100%;
                }
            `,
        },
        img: {
            css: css`
                height: auto;
                width: 100%;
            `,
        },
    },
    productPromotionTextBannerStyles: {
        wrapper: {
            css: css`
                display: flex;
                position: static;
                z-index: 1;
                padding: 0.25em 0.5em;
                text-align: center;
                align-items: center;
            `,
        },
        label: {
            size: TYPOGRAPHY.size.veryTiny,
            color: "white",
            css: css`
                display: flex;
                font-weight: bold;
                text-transform: uppercase;
                margin-bottom: 0;
            `,
        },
    },
    cartMobileHidden: {
        above: "sm",
        css: css`
            width: 100%;
        `,
    },
    cartDesktopHidden: {
        below: "md",
        css: css`
            width: 100%;
        `,
    },
    productAvailabilityStyles: {
        container: {
            css: css`
                margin-bottom: ${TYPOGRAPHY.spacing.small};

                div {
                    padding-top: 0 !important;
                    padding-bottom: 0 !important;
                }

                span {
                    font-size: ${TYPOGRAPHY.size.tiny};
                    width: auto;
                }

                /* ${IconWrapper} { // This doesn't work here. */
                [class^="IconWrapper"] {
                    margin-right: 4px !important;

                    svg {
                        height: 14px;
                        width: 14px;
                    }
                }
            `,
        },
    },
};

const ProductCarouselProductSlim = ({
    carouselId,
    product,
    labelsVisible,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showAvailability,
    showPrice,
    showAddToCart,
    showAddToList,
    updateProductInfo,
    extendedStyles,
    productInfo,
    condensedActionsTemplate,
    isQuickOrder,
    parentProduct,
    mobileAddOverride,
    desktopAddOverride,
    analyticsId, // Store Custom Code
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductSlimStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const mobileAddToCartLabel = mobileAddOverride || "Add to cart"; // Store Custom Code
    const desktopAddToCartLabel = desktopAddOverride || "Add to cart"; // Store Custom Code

    return (
        <>
            <ProductContext.Provider value={productContext}>
                <GridContainer {...styles.container}>
                    <GridItem {...styles.imageGridItem}>
                        {/* Store Custom Code */}
                        {showImage && (
                            <ProductImage
                                product={productContext}
                                extendedStyles={styles.productImageStyles}
                                analyticsId={analyticsId}
                            />
                        )}
                    </GridItem>
                    <GridItem {...styles.productDetailsGridItem}>
                        {/* Store Custom Code */}
                        <ProductPromotionTextBanner
                            extendedStyles={styles.productPromotionTextBannerStyles}
                            product={productContext}
                        ></ProductPromotionTextBanner>
                        {showBrand && product.brand && (
                            <StyledWrapper {...styles.productBrandWrapper}>
                                <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                            </StyledWrapper>
                        )}
                        {/* Store Custom Code */}
                        {showTitle && (
                            <ProductDescription
                                product={productContext}
                                extendedStyles={styles.productDescriptionStyles}
                                analyticsId={analyticsId}
                            />
                        )}
                        {showPartNumbers && (
                            <ProductPartNumbers
                                productNumber={product.productNumber}
                                customerProductNumber={product.customerProductNumber}
                                manufacturerItem={product.manufacturerItem}
                                showCustomerName={false}
                                showManufacturerItem={false}
                                showLabel={true}
                                extendedStyles={styles.productPartNumbersStyles}
                            />
                        )}
                        {showAvailability && (
                            <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                        )}
                    </GridItem>
                </GridContainer>
                <GridContainer {...styles.containerLower}>
                    <GridItem {...styles.productBreakPriceGridItem}>
                        <ProductQuantityBreakPricing
                            extendedStyles={styles.productQuantityBreakPricingStyles}
                            isModal={true}
                        />
                    </GridItem>
                    <GridItem {...styles.pricingGridItem}>
                        {showPrice && (
                            <StyledWrapper {...styles.productPriceWrapper}>
                                <VisuallyHidden as="label" htmlFor={`${carouselId}-uom`} id={`${carouselId}-uom-label`}>
                                    {translate("U/M")}
                                </VisuallyHidden>
                                <ProductPrice
                                    showLabel={false}
                                    product={productContext}
                                    extendedStyles={styles.productPriceStyles}
                                    usePriceMinusCorePrice={true}
                                    showEcoLabel={true}
                                />
                                {product.unitOfMeasures && (
                                    <ProductUnitOfMeasureSelect
                                        labelOverride=""
                                        extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                        id={`${carouselId}-uom`}
                                    />
                                )}
                            </StyledWrapper>
                        )}
                    </GridItem>
                    {isQuickOrder && parentProduct ? (
                        <GridItem {...styles.productSwapGridItem}>
                            <QuickOrderProductSwapButton
                                parentProduct={parentProduct}
                                extendedStyles={styles.productSwapButtonStyles}
                            />
                        </GridItem>
                    ) : (
                        <GridItem {...styles.productCartGridItem}>
                            {showAddToCart && (
                                <GridContainer {...styles.addToCartContainer}>
                                    <GridItem {...styles.qtyGridItem}>
                                        <VisuallyHidden
                                            as="label"
                                            htmlFor={`${carouselId}-qty`}
                                            id={`${carouselId}-qty-label`}
                                        >
                                            {translate("QTY")}
                                        </VisuallyHidden>
                                        <ProductQuantityOrdered
                                            labelOverride=""
                                            extendedStyles={styles.productQuantityOrderedStyles}
                                            id={`${carouselId}-qty`}
                                        />
                                    </GridItem>
                                    <GridItem {...styles.addToCartButtonGridItem}>
                                        <Hidden {...styles.cartMobileHidden}>
                                            <ProductAddToCartButton
                                                data-test-selector="addToCartBtn"
                                                extendedStyles={styles.productAddToCartButtonStyles}
                                                labelOverride={translate(mobileAddToCartLabel)}
                                                analyticsId={analyticsId}
                                            />
                                        </Hidden>
                                        <Hidden {...styles.cartDesktopHidden}>
                                            {/* Store Custom Code */}
                                            <ProductAddToCartButton
                                                data-test-selector="addToCartBtn"
                                                extendedStyles={styles.productAddToCartButtonStyles}
                                                labelOverride={translate(desktopAddToCartLabel)}
                                                analyticsId={analyticsId}
                                            />
                                        </Hidden>
                                    </GridItem>
                                </GridContainer>
                            )}
                        </GridItem>
                    )}
                </GridContainer>
                {/* Disabled unused feature, kept in place for easier updates */}
                {/* {condensedActionsTemplate ? (
                        <GridItem {...styles.condensedActionsTemplateGridItem}>
                            {condensedActionsTemplate(product)}
                        </GridItem>
                    ) : (
                        []
                    )} */}
                {/* {showAddToList && (
                    <ProductAddToListLink
                        data-test-selector="addToListLink"
                        extendedStyles={styles.productAddToListLinkStyles}
                    />
                )} */}
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProductSlim);
